import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Pagination from '../../components/Pagination';
import VoiceInput from '../../components/VoiceInput/VoiceInput';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import LoginModal from '../../modals/LoginModal';
import RegisterModal from '../../modals/RegisterModal';
import Footer from '../../common/layout/Footer';
import { LocationIndicator } from '../../components/LocationIndicator/LocationIndicator';
import Tooltip from '../../components/Tooltip';
import { getBusinesses } from '../../services/businessService';
import { getMasters, Master } from '../../services/mastersService';
import { useLocation } from '../../contexts/LocationContext';
import { openGoogleMaps } from '../../utils/mapsUtils';
import { formatPhoneNumber, getCleanPhoneNumber } from '../../utils/phoneUtils';
import BusinessMastersSwitch, { ViewMode } from '../../components/BusinessMastersSwitch/BusinessMastersSwitch';
import RatingIcon from '../../components/RatingIcon/RatingIcon';
import './PublicHomePage.css';

interface Business {
  id: string;
  name: string;
  categories: string[];
  address: string;
  phone: string;
  rating: number;
  image: string;
  // services: string[]; // Commented out - not needed for business cards
  isActive?: boolean;
}

const PublicHomePage: React.FC = () => {
  const { language } = useLanguage();
  const { currentCity, isLocationReady } = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [masters, setMasters] = useState<Master[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('business');
  const abortControllerRef = useRef<AbortController>();

  const t = (key: any) => translate(key, language as 'en' | 'ua');

  // Функція для розбиття тексту на рядки по 50 символів
  const splitText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    for (const word of words) {
      if ((currentLine + ' ' + word).length <= maxLength) {
        currentLine = currentLine ? currentLine + ' ' + word : word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Якщо слово довше за maxLength, розбиваємо його
          for (let i = 0; i < word.length; i += maxLength) {
            lines.push(word.substring(i, i + maxLength));
          }
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines.join('\n');
  };

  // Дебаунсинг для пошукового запиту
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Відновлення режиму перегляду з localStorage при завантаженні
  useEffect(() => {
    const savedViewMode = localStorage.getItem('public_view_mode') as ViewMode;
    if (savedViewMode && (savedViewMode === 'business' || savedViewMode === 'masters')) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Отримання майстрів
  const fetchMasters = useCallback(async (
    city: string | undefined,
    page: number,
    limit: number,
    signal?: AbortSignal
  ) => {
    try {
      setError(null);
      const data = await getMasters(city, page, limit, { 
        signal, 
        search: debouncedSearchQuery,
        language: language === 'en' ? 'en' : undefined 
      });

      if (!signal?.aborted) {
        setMasters(data.masters);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      console.error('Error fetching masters:', error);
      if (!signal?.aborted) {
        setError('Не вдалося завантажити майстрів');
        setMasters([]);
        setTotalPages(1);
      }
    }
  }, [debouncedSearchQuery, language]);

  // Отримання бізнесів з кращою обробкою помилок
  const fetchBusinesses = useCallback(async (
    city: string | undefined,
    page: number,
    limit: number,
    signal?: AbortSignal
  ) => {
    try {
      setError(null);
      const data = await getBusinesses(city, page, limit, { 
        signal, 
        language: language === 'en' ? 'en' : undefined 
      });

      if (!signal?.aborted) {
        setBusinesses(data?.businesses ?? []);
        setTotalPages(data?.totalPages ?? 1);
      }
    } catch (err: any) {
      if (!signal?.aborted) {
        console.error('Помилка завантаження бізнесів:', err);
        setError('Не вдалося завантажити список бізнесів. Спробуйте пізніше.');
        setBusinesses([]);
        setTotalPages(1);
      }
    }
  }, [language]);

  // Ефект для завантаження даних залежно від режиму перегляду
  useEffect(() => {
    // Чекаємо на готовність локації перед першим запитом
    if (!isLocationReady) {
      return;
    }

    // Скасування попереднього запиту
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);

    if (viewMode === 'business') {
      fetchBusinesses(currentCity || undefined, currentPage, rowsPerPage, abortController.signal)
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
          }
        });
    } else {
      fetchMasters(currentCity || undefined, currentPage, rowsPerPage, abortController.signal)
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
          }
        });
    }

    return () => abortController.abort();
  }, [viewMode, currentCity, currentPage, rowsPerPage, isLocationReady, fetchBusinesses, fetchMasters]);

  // Мемоїзована фільтрація бізнесів за пошуком (на клієнті)
  const filteredBusinesses = useMemo(() => {
    const isActive = (business: Business) => business.isActive !== false;

    // Якщо пошуковий запит порожній, повертаємо всі активні бізнеси
    if (!debouncedSearchQuery.trim()) {
      return businesses.filter(isActive);
    }

    // Нормалізація тексту - видаляємо коми і зайві пробіли
    const normalizeText = (text: string) => text.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();

    // Нормалізація телефону - видаляємо всі нецифрові символи
    const normalizePhone = (phone: string) => phone.replace(/\D/g, '');

    const normalizedQuery = normalizeText(debouncedSearchQuery);
    const normalizedQueryPhone = normalizePhone(debouncedSearchQuery);

    return businesses.filter(business => {
      if (!isActive(business)) return false;

      const normalizedName = normalizeText(business.name);
      const normalizedAddress = normalizeText(business.address);
      const normalizedPhone = normalizePhone(business.phone);

      // Перевіряємо категорії
      const matchesCategory = business.categories && business.categories.some(category =>
        normalizeText(category).includes(normalizedQuery)
      );

      // Перевіряємо відповідність за назвою, категорією або адресою
      const matchesQuery =
        normalizedName.includes(normalizedQuery) ||
        normalizedAddress.includes(normalizedQuery) ||
        (normalizedQueryPhone.length > 0 && normalizedPhone.includes(normalizedQueryPhone)) ||
        matchesCategory;

      return matchesQuery;
    });
  }, [businesses, debouncedSearchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRowsPerPageChange = (limit: number) => {
    setRowsPerPage(limit);
    setCurrentPage(1);
  };

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  const handleRegisterClick = () => {
    setIsRegisterModalOpen(true);
  };

  /**
   * Обробник кліку на адресу - відкриває Google Maps
   * @param address - Адреса у форматі "місто, вулиця, номер"
   * @param city - Місто (для перепевної передачі)
   */
  const handleAddressClick = (address: string, city?: string) => {
    // Спробуємо розібрати адресу
    // Формат: "місто, вулиця, номер" або "вулиця, номер"
    
    if (address.includes(',')) {
      const parts = address.split(',').map(part => part.trim());
      
      if (parts.length >= 3) {
        // Формат: місто, вулиця, номер
        const addressCity = city || parts[0];
        const street = parts[1];
        const building = parts[2];
        console.log(`🗺️ Відкриття Google Maps: ${street} ${building}, ${addressCity}`);
        openGoogleMaps(addressCity, street, building);
      } else if (parts.length === 2) {
        // Формат: вулиця, номер або місто, вулиця
        const addressCity = city || currentCity || 'Ukraine';
        
        // Перевіримо, чи друга частина - номер будинку
        const lastPart = parts[1];
        const isBuilding = /^\d+\w*$/.test(lastPart);
        
        if (isBuilding) {
          const street = parts[0];
          const building = lastPart;
          console.log(`🗺️ Відкриття Google Maps: ${street} ${building}, ${addressCity}`);
          openGoogleMaps(addressCity, street, building);
        } else {
          // Це вулиця без номера
          const addressPart = parts.join(' ');
          console.log(`🗺️ Відкриття Google Maps для: ${addressPart}`);
          openGoogleMaps(addressCity, addressPart, '');
        }
      } else {
        // Одна частина - просто адреса
        const addressCity = city || currentCity || 'Ukraine';
        console.log(`🗺️ Відкриття Google Maps для: ${address}`);
        openGoogleMaps(addressCity, address, '');
      }
    } else {
      // Якщо нема коми, спробуємо витягти місто з контексту
      const addressCity = city || currentCity || 'Ukraine';
      console.log(`🗺️ Відкриття Google Maps для: ${address} у місті ${addressCity}`);
      openGoogleMaps(addressCity, address, '');
    }
  };

  // Показуємо завантаження, поки локація не готова
  if (!isLocationReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Визначення локації...</p>
        </div>
      </div>
    );
  }

  // Loading skeleton component
  const LoadingSkeleton: React.FC = () => (
    <div className="salons-grid" aria-label="Завантаження бізнесів">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="salon-card skeleton" aria-hidden="true">
          <div className="skeleton-image"></div>
          <div className="skeleton-content">
            <div className="skeleton-title"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text short"></div>
            <div className="skeleton-badges">
              <div className="skeleton-badge"></div>
              <div className="skeleton-badge"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="public-home-page">
      {/* Header */}
      <header className="public-header" role="banner">
        <div className="header-container">
          <div className="header-logo">
            <i className="bi bi-calendar-check-fill" aria-hidden="true"></i>
            <span className="logo-text">VisitPro</span>
          </div>

          {/* Location Indicator */}
          <div className="location-container">
            <LocationIndicator />
          </div>

          {/* Mobile Login Icon */}
          <button
            className="mobile-login-icon"
            onClick={handleLoginClick}
            type="button"
            aria-label="Увійти в систему"
          >
            <i className="bi bi-box-arrow-in-right"></i>
          </button>

          <div className="header-search" role="search">
            <label htmlFor="business-search" className="sr-only">
              {t('public.search.placeholder')}
            </label>
            <i className="bi bi-search" aria-hidden="true"></i>
            <input
              id="business-search"
              type="text"
              placeholder={t('public.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              aria-describedby="search-help"
              autoComplete="off"
            />
            <div id="search-help" className="sr-only">
              Пошук за назвою, адресою, телефоном або категорією бізнесу
            </div>
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
                title={t('public.search.clear')}
                aria-label={t('public.search.clear')}
                type="button"
              >
                <i className="bi bi-x-circle" aria-hidden="true"></i>
              </button>
            )}
            {!searchQuery && (
              <VoiceInput
                onTranscriptChange={(transcript) => setSearchQuery(transcript)}
                defaultMode="fast"
                className="search-voice-input"
              />
            )}
          </div>

          <nav className="header-actions" aria-label="Користувацькі дії">
            <button
              className="header-btn login-btn"
              onClick={handleLoginClick}
              type="button"
            >
              <i className="bi bi-box-arrow-in-right" aria-hidden="true"></i>
              <span>{t('public.header.login')}</span>
            </button>
            <button
              className="header-btn register-btn"
              onClick={handleRegisterClick}
              type="button"
            >
              <i className="bi bi-person-plus" aria-hidden="true"></i>
              <span>{t('public.header.register')}</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main
        className={`salons-container ${isLoginModalOpen || isRegisterModalOpen ? 'blurred' : ''}`}
        role="main"
        aria-labelledby="business-heading"
      >
        {/* Business/Masters Switch */}
        <div className="page-switch-container">
          <BusinessMastersSwitch 
            viewMode={viewMode} 
            onViewModeChange={setViewMode} 
          />
        </div>

        <h1 id="business-heading" className="sr-only">
          {viewMode === 'business' ? 'Каталог бізнесів та салонів краси' : 'Каталог майстрів'}
        </h1>

        {/* Error State */}
        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            <i className="bi bi-exclamation-triangle"></i>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
              type="button"
            >
              Спробувати знову
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && !error && <LoadingSkeleton />}

        {/* Business/Masters Grid */}
        {!isLoading && !error && (
          <section className="business-section" aria-labelledby="business-grid-heading">
            <h2 id="business-grid-heading" className="sr-only">
              {viewMode === 'business' ? 'Список доступних бізнесів' : 'Список доступних майстрів'}
            </h2>

            <div className="salons-grid">
              {viewMode === 'business' ? (
                filteredBusinesses.length > 0 ? (
                  filteredBusinesses.map((business) => (
                  <article
                    key={business.id}
                    className="salon-card"
                    role="article"
                    aria-labelledby={`business-${business.id}-title`}
                  >
                    <div className="salon-image">
                      {business.image ? (
                        <img
                          src={business.image}
                          alt={`Фото бізнесу ${business.name}`}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="image-placeholder"
                          role="img"
                          aria-label="Фото недоступне"
                        >
                          <ImageIcon style={{ fontSize: 96, color: '#bdbdbd' }} />
                        </div>
                      )}
                      {business.rating && (
                        <RatingIcon rating={business.rating} />
                      )}
                    </div>
                    <div className="salon-info">
                      <h3 id={`business-${business.id}-title`} className="salon-name" style={{ display: 'flex', alignItems: 'center' }}>
                        {business.name} <OpenInNewIcon fontSize="small" color="disabled" style={{ marginLeft: 4 }} />
                      </h3>

                      <address className="salon-address">
                        <i className="bi bi-geo-alt" aria-hidden="true"></i>
                        <button
                          onClick={() => handleAddressClick(business.address, currentCity || undefined)}
                          className="address-link"
                          type="button"
                          aria-label={`Відкрити адресу ${business.address} у Google Maps`}
                        >
                          {business.address}
                        </button>
                      </address>

                      <div className="salon-phone">
                        <i className="bi bi-telephone" aria-hidden="true"></i>
                        <a
                          href={`tel:${getCleanPhoneNumber(business.phone)}`}
                          className="phone-link"
                          aria-label={`Зателефонувати до ${business.name}: ${formatPhoneNumber(business.phone)}`}
                        >
                          {formatPhoneNumber(business.phone)}
                        </a>
                      </div>

                      <div className="salon-categories" role="list" aria-label="Категорії бізнесу">
                        {Array.isArray(business.categories) && business.categories.length > 0 ? (
                          business.categories.map((category, index) => {
                            // Визначаємо іконку залежно від категорії
                            const isHairService = category.toLowerCase().includes('стриж') || 
                                                 category.toLowerCase().includes('перукар') ||
                                                 category.toLowerCase().includes('hair') ||
                                                 category.toLowerCase().includes('коса') ||
                                                 category.toLowerCase().includes('зачіска');
                            
                            return (
                              <span
                                key={index}
                                className="category-badge"
                                role="listitem"
                                aria-label={`Категорія: ${category}`}
                              >
                                <i className={`bi ${isHairService ? 'bi-scissors' : 'bi-tag'}`} aria-hidden="true"></i>
                                {category}
                              </span>
                            );
                          })
                        ) : (
                          <span className="category-badge no-categories" role="listitem">
                            Без категорій
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <div className="no-results" role="status" aria-live="polite">
                  <i className="bi bi-search" aria-hidden="true"></i>
                  <p>{debouncedSearchQuery ? 'За вашим запитом нічого не знайдено' : t('public.salons.noResults')}</p>
                  {debouncedSearchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="clear-search-link"
                      type="button"
                    >
                      Очистити пошук
                    </button>
                  )}
                  <div className="no-results-cta">
                    <p>{t('public.salons.noResultsCTA')}</p>
                    <button
                      className="cta-register-btn"
                      onClick={handleRegisterClick}
                      type="button"
                    >
                      {t('public.header.register')}
                    </button>
                  </div>
                </div>
              )
              ) : (
                // Masters view
                masters.length > 0 ? (
                  masters.map((master) => (
                    <article
                      key={master.id}
                      className="salon-card master-card"
                      role="article"
                      aria-labelledby={`master-${master.id}-title`}
                    >
                      <div className="salon-image">
                        {master.avatar ? (
                          <img
                            src={master.avatar}
                            alt={`Фото майстра ${master.fullName}`}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="image-placeholder"
                            role="img"
                            aria-label="Фото недоступне"
                          >
                            <i className="bi bi-person-circle" style={{ fontSize: 96, color: '#bdbdbd' }}></i>
                          </div>
                        )}
                        {master.rating && (
                          <RatingIcon rating={master.rating} />
                        )}
                      </div>
                      <div className="salon-info">
                        <h3 id={`master-${master.id}-title`} className="salon-name" style={{ display: 'flex', alignItems: 'center' }}>
                          {master.fullName} <OpenInNewIcon fontSize="small" color="primary" style={{ marginLeft: 4 }} />
                        </h3>

                        {master.experience && (
                          <div className="master-experience">
                            <i className="bi bi-clock-history" aria-hidden="true"></i>
                            <Tooltip content={`Досвід:\n${splitText(master.experience)}`} position="top" align="center">
                              <span>Досвід: {master.experience.length > 10 ? `${master.experience.substring(0, 10)}...` : master.experience}</span>
                            </Tooltip>
                          </div>
                        )}

                        {master.businessAddress && (
                          <address className="salon-address">
                            <i className="bi bi-geo-alt" aria-hidden="true"></i>
                            <button
                              onClick={() => handleAddressClick(master.businessAddress || '', master.city || '')}
                              className="address-link"
                              type="button"
                              aria-label={`Відкрити адресу ${master.businessAddress} у Google Maps`}
                            >
                              {master.businessAddress}
                            </button>
                          </address>
                        )}

                        {master.phone && (
                          <div className="salon-phone">
                            <i className="bi bi-telephone" aria-hidden="true"></i>
                            <a
                              href={`tel:${getCleanPhoneNumber(master.phone)}`}
                              className="phone-link"
                              aria-label={`Зателефонувати до ${master.fullName}: ${formatPhoneNumber(master.phone)}`}
                            >
                              {formatPhoneNumber(master.phone)}
                            </a>
                          </div>
                        )}

                        <div className="salon-categories" role="list" aria-label="Категорії бізнесу">
                          {Array.isArray(master.categories) && master.categories.length > 0 ? (
                            master.categories.map((category, index) => {
                              // Визначаємо іконку залежно від категорії
                              const isHairService = category.toLowerCase().includes('стриж') || 
                                                   category.toLowerCase().includes('перукар') ||
                                                   category.toLowerCase().includes('hair') ||
                                                   category.toLowerCase().includes('коса') ||
                                                   category.toLowerCase().includes('зачіска');
                              
                              return (
                                <span
                                  key={index}
                                  className="category-badge"
                                  role="listitem"
                                  aria-label={`Категорія: ${category}`}
                                >
                                  <i className={`bi ${isHairService ? 'bi-scissors' : 'bi-tag'}`} aria-hidden="true"></i>
                                  {category}
                                </span>
                              );
                            })
                          ) : (
                            <span className="category-badge no-categories" role="listitem">
                              Без категорій
                            </span>
                          )}
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="no-results" role="status" aria-live="polite">
                    <i className="bi bi-search" aria-hidden="true"></i>
                    <p>{debouncedSearchQuery ? 'За вашим запитом майстрів не знайдено' : 'Майстрів не знайдено'}</p>
                    {debouncedSearchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="clear-search-link"
                        type="button"
                      >
                        Очистити пошук
                      </button>
                    )}
                    <div className="no-results-cta">
                      <p>Зареєструйтеся, щоб знайти найкращих майстрів</p>
                      <button
                        className="cta-register-btn"
                        onClick={handleRegisterClick}
                        type="button"
                      >
                        {t('public.header.register')}
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label="Навігація сторінками">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </nav>
            )}
          </section>
        )}
      </main>

      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {/* Register Modal */}
      <RegisterModal 
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onOpenLogin={() => {
          setIsRegisterModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />

      {/* Floating Action Button Menu for Mobile */}
      <div className={`floating-action-menu ${isFabMenuOpen ? 'open' : ''}`}>
        <div className="fab-menu-items">
          <button
            className="fab-menu-item"
            onClick={() => {
              setIsLoginModalOpen(true);
              setIsFabMenuOpen(false);
            }}
            title={t('public.header.login')}
          >
            <i className="bi bi-box-arrow-in-right"></i>
          </button>
          <button
            className="fab-menu-item register"
            onClick={() => {
              setIsRegisterModalOpen(true);
              setIsFabMenuOpen(false);
            }}
            title={t('public.header.register')}
          >
            <i className="bi bi-person-plus"></i>
          </button>
        </div>
        <button
          className={`fab-toggle-btn ${isFabMenuOpen ? 'open' : ''}`}
          onClick={() => setIsFabMenuOpen(!isFabMenuOpen)}
        >
          <i className="bi bi-plus-lg"></i>
        </button>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PublicHomePage;
